import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  DrawerBody,
  ActivityFeed,
  eventToActivityItem,
  Skeleton,
  SkeletonText,
  EmptyState,
  Button,
  AssuranceBadge,
  Pill,
} from "@/design/components";
import { useSeed, useMoney } from "@/domain/hooks";
import { traceApi } from "@/domain/api";
import {
  type TraceTarget,
  type TraceResult,
  nextHopCards,
} from "@/domain/trace";
import { useTraceNode, resolveOpenAncestry } from "./trace/useTraceNode";
import { TracePathRibbon, type TraceRibbonEntry } from "./trace/TracePathRibbon";
import { TraceSiblingShelf } from "./trace/TraceSiblingShelf";
import { TraceMoneyStrip, type MoneyStripItem } from "./trace/TraceMoneyStrip";
import { EvidenceInline } from "./trace/EvidenceInline";
import { TraceNextHopCards } from "./trace/TraceNextHopCards";

/** Where the trace was opened from, so the drawer can say "Traced from …". */
type TraceOpts = { from?: string };

type TraceCtx = { openTrace: (t: TraceTarget, opts?: TraceOpts) => void };
const Ctx = createContext<TraceCtx>({ openTrace: () => {} });

const SEEN_KEY = "il-trace-seen";

function readSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return true; // if storage is unavailable, suppress the hint rather than nag every open
  }
}

function markSeen() {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* non-fatal */
  }
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Best-effort, non-blocking audit log — never lets a logging failure interrupt navigation. */
function logTraceEvent(input: Parameters<typeof traceApi.logEvent>[0]) {
  traceApi.logEvent(input).catch(() => {
    /* audit logging is best-effort; a failure here must never block the trace */
  });
}

function keyFor(t: TraceTarget): string {
  return `${t.kind}:${t.id}`;
}

/** Any component can call openTrace(target) to trace a figure to its source. */
export function useTrace(): TraceCtx {
  return useContext(Ctx);
}

export function TraceProvider({ children }: { children: ReactNode }) {
  const seed = useSeed();
  const [stack, setStack] = useState<TraceTarget[]>([]);
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [showHint, setShowHint] = useState(false);
  const [labelCache, setLabelCache] = useState<Record<string, string>>({});
  const [siblingWalks, setSiblingWalks] = useState(0);
  const triggerElRef = useRef<HTMLElement | null>(null);

  const cacheLabel = useCallback((t: TraceTarget, label: string) => {
    setLabelCache((prev) => (prev[keyFor(t)] === label ? prev : { ...prev, [keyFor(t)]: label }));
  }, []);

  const openTrace = useCallback(
    (t: TraceTarget, opts?: TraceOpts) => {
      // Radix Dialog only traps focus once mounted; capture the trigger now so
      // close() can return focus to it later regardless of what re-renders happen
      // in between (spec §8.2 — "on close, focus returns to the element that
      // opened the trace").
      triggerElRef.current = document.activeElement as HTMLElement | null;
      setFrom(opts?.from);
      setSiblingWalks(0);
      if (!readSeen()) setShowHint(true);
      logTraceEvent({ eventType: "trace.opened", entityType: t.kind, entityId: t.id, payload: { openedFrom: opts?.from ?? null } });

      resolveOpenAncestry(seed, t).then((crumbs) => {
        const chain = crumbs.map((c) => c.target).filter((x): x is TraceTarget => !!x);
        setStack(chain.length ? chain : [t]);
        setLabelCache((prev) => {
          const next = { ...prev };
          for (const c of crumbs) if (c.target) next[keyFor(c.target)] = c.label;
          return next;
        });
      });
    },
    [seed],
  );

  /** Walk DOWN/UP to a new node — appended to the path. */
  const walkTo = (t: TraceTarget) => {
    const fromNode = stack[stack.length - 1];
    setStack((s) => [...s, t]);
    logTraceEvent({ eventType: "trace.walked_to", entityType: t.kind, entityId: t.id, payload: { fromNodeId: fromNode?.id ?? null } });
  };

  /** Ribbon click / browser-back — truncate the path to that node. */
  const goTo = (i: number) => setStack((s) => s.slice(0, i + 1));

  /** Sibling chip click — same depth, replaces the last node in place (spec §8.4). */
  const walkSibling = (t: TraceTarget) => {
    const fromNode = stack[stack.length - 1];
    setStack((s) => [...s.slice(0, -1), t]);
    setSiblingWalks((n) => n + 1);
    logTraceEvent({ eventType: "trace.walked_to", entityType: t.kind, entityId: t.id, payload: { fromNodeId: fromNode?.id ?? null, sibling: true } });
  };

  const close = () => {
    logTraceEvent({ eventType: "trace.closed", payload: { depthReached: stack.length, siblingWalks } });
    setStack([]);
    setFrom(undefined);
    setShowHint(false);
    triggerElRef.current?.focus();
  };

  const dismissHint = () => {
    setShowHint(false);
    markSeen();
  };

  return (
    <Ctx.Provider value={{ openTrace }}>
      {children}
      <Dialog.Root open={stack.length > 0} onOpenChange={(o) => !o && close()}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 z-40 animate-fade-in backdrop-blur-[2px]"
            // Literal rgba rather than bg-ink/NN: `ink` is a bare var(--x) token
            // (tailwind.config.ts) and Tailwind can't compute an opacity
            // modifier against a color it can't statically parse — the same
            // bug fixed for the ProjectSummaryPage hero and EvidenceInline's
            // gradient earlier. rgb triplet matches the --ink-900 design token.
            style={{ backgroundColor: "rgba(15,17,41,0.45)" }}
          />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[min(920px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in outline-none">
            {stack.length > 0 && (
              <TraceBody
                stack={stack}
                from={from}
                showHint={showHint}
                labelCache={labelCache}
                onDismissHint={dismissHint}
                onCrumb={goTo}
                onWalk={walkTo}
                onSiblingWalk={walkSibling}
                onNodeResolved={cacheLabel}
                onClose={close}
              />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Ctx.Provider>
  );
}

function TraceBody({
  stack,
  from,
  showHint,
  labelCache,
  onDismissHint,
  onCrumb,
  onWalk,
  onSiblingWalk,
  onNodeResolved,
  onClose,
}: {
  stack: TraceTarget[];
  from?: string;
  showHint: boolean;
  labelCache: Record<string, string>;
  onDismissHint: () => void;
  onCrumb: (i: number) => void;
  onWalk: (t: TraceTarget) => void;
  onSiblingWalk: (t: TraceTarget) => void;
  onNodeResolved: (t: TraceTarget, label: string) => void;
  onClose: () => void;
}) {
  const moneyFn = useMoney();
  const target = stack[stack.length - 1];
  const root = stack[0];
  const { data: result, isLoading, isError, refetch } = useTraceNode(target);

  useEffect(() => {
    if (result && target) onNodeResolved(target, result.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, target.kind, target.id]);

  // On every drill (stack change) move focus to the header so a screen-reader and keyboard
  // user is taken to the new node, not stranded on the row they just left.
  const headerRef = useRef<HTMLDivElement>(null);
  const stackKey = stack.map((t) => `${t.kind}:${t.id}`).join("/");
  useEffect(() => {
    headerRef.current?.focus();
  }, [stackKey]);

  // Restrained drill transition: the body content starts a hair low and faded, then settles.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setEntered(true);
      return;
    }
    setEntered(false);
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [stackKey]);

  // Keyboard: ← walks back one ribbon step; → walks to the single down-child, if exactly
  // one exists (spec §8.2). Esc/focus-trap are handled by the underlying Radix Dialog.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowLeft" && stack.length > 1) {
        e.preventDefault();
        onCrumb(stack.length - 2);
      } else if (e.key === "ArrowRight" && result?.down.length === 1 && result.down[0].target) {
        e.preventDefault();
        onWalk(result.down[0].target);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [stack, result, onCrumb, onWalk]);

  const ribbonEntries: TraceRibbonEntry[] = stack.map((t, i) => ({
    target: t,
    label: labelCache[`${t.kind}:${t.id}`] ?? "…",
    amount: i === stack.length - 1 && result?.amount !== undefined ? moneyFn(result.amount) : undefined,
    edge: i > 0 ? edgeLabelFor(stack[i - 1].kind, t.kind) : undefined,
  }));

  const rootLabel = labelCache[`${root.kind}:${root.id}`] ?? "…";

  return (
    <>
      {/*
        A plain div, not the shared DrawerHeader — DrawerHeader always renders
        its own Dialog.Close X icon (styled text-faint, for a light header)
        alongside whatever children it's given. Nesting this fully custom dark
        header inside it produced a redundant second close control that was
        also nearly invisible against bg-ink (text-faint on a dark background).
        This single "Close trace" button is the only close affordance here;
        Esc and the overlay click still close via Dialog.Root's onOpenChange.
      */}
      <div
        ref={headerRef}
        tabIndex={-1}
        className="flex items-start justify-between gap-4 rounded-t-card bg-ink px-6 py-4 text-onDark outline-none"
      >
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-2 py-0.5 font-mono text-[10.5px] text-onDark">
            Trace source · locked scope
          </span>
          <Dialog.Title className="mt-2 text-sm font-medium text-onDark">
            From <strong className="font-semibold">{rootLabel}</strong>
            {result && stack.length === 1 && result.amount !== undefined ? ` · ${moneyFn(result.amount)}` : ""}
          </Dialog.Title>
          <Dialog.Description className="sr-only">
            Follow this figure to its source. Every read is scoped to this trace's own graph.
          </Dialog.Description>
          <p aria-live="polite" className="mt-1 font-mono text-[10.5px] text-white/50">
            {stack.length - 1} hop{stack.length - 1 === 1 ? "" : "s"} walked · every read scoped to this trace&rsquo;s graph
          </p>
          {from && <p className="mt-0.5 text-[11px] text-white/40">Traced from {from}</p>}
        </div>
        <button
          onClick={onClose}
          className="shrink-0 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-onDark transition-colors hover:bg-white/[0.12]"
        >
          Close trace
        </button>
      </div>

      <div className="border-b border-line bg-gradient-to-b from-bg to-card px-5 py-3">
        <TracePathRibbon entries={ribbonEntries} onWalk={onCrumb} />
      </div>

      <DrawerBody className="space-y-0 p-0">
        <div className="px-6 py-5">
          {showHint && <FirstUseHint onDismiss={onDismissHint} />}

          {isLoading ? (
            <TraceSkeleton />
          ) : isError || !result ? (
            <EmptyState
              title="Something went wrong loading this node."
              body="Try again, or close the trace and reopen it."
              cta={<Button size="sm" onClick={() => refetch()}>Try again</Button>}
            />
          ) : (
            <div
              key={stackKey}
              className={`space-y-5 transition duration-150 ease-out ${
                entered ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0"
              }`}
            >
              <div className="space-y-2.5">
                <TraceMoneyStrip items={moneyStripFor(target, result, moneyFn)} />
                {result.meta.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {result.meta.map((m) => (
                      <Pill key={m} variant="outline">{m}</Pill>
                    ))}
                  </div>
                )}
              </div>

              {result.up.length > 0 && (
                <TraceSection label="Part of">
                  {result.up.map((n, i) => (
                    <TraceRow key={i} node={n} money={moneyFn} onNavigate={onWalk} />
                  ))}
                </TraceSection>
              )}

              {result.down.length > 0 && (
                <TraceSection label={`Made up of ${result.down.length} ${downNoun(target.kind)}`}>
                  {result.down.map((n, i) => (
                    <TraceRow key={i} node={n} money={moneyFn} onNavigate={onWalk} />
                  ))}
                </TraceSection>
              )}

              {result.related && result.related.length > 0 && (
                <TraceSection label={target.kind === "spend" ? "Justifies" : "Justified by"}>
                  {result.related.map((n, i) => (
                    <TraceRow key={i} node={n} money={moneyFn} onNavigate={onWalk} />
                  ))}
                </TraceSection>
              )}

              {result.siblings && (
                <TraceSiblingShelf
                  siblings={result.siblings}
                  parentName={result.up[0]?.label}
                  entityKind={target.kind}
                  money={moneyFn}
                  onWalk={onSiblingWalk}
                />
              )}

              <EvidenceSection result={result} />

              {result.audit.length > 0 && (
                <TraceSection label="Activity">
                  <ActivityFeed items={result.audit.map((e) => eventToActivityItem(e))} density="compact" />
                </TraceSection>
              )}
            </div>
          )}
        </div>

        {result && (
          <TraceNextHopCards hops={nextHopCards(target, result, root, rootLabel)} onWalk={onWalk} />
        )}
      </DrawerBody>
    </>
  );
}

/** Edge label between two ribbon nodes, e.g. "made up of" / "justifies" / "sits inside". */
function edgeLabelFor(parentKind: TraceTarget["kind"], childKind: TraceTarget["kind"]): string {
  if (childKind === "subProject" || childKind === "spend") return "made up of";
  if (parentKind === "spend" && childKind === "disbursement") return "justifies";
  if (childKind === "milestone") return "part of";
  return "part of";
}

function downNoun(kind: TraceTarget["kind"]): string {
  switch (kind) {
    case "disbursement":
      return "spend asks";
    case "milestone":
      return "spend asks";
    case "project":
      return "sub-projects";
    case "subProject":
      return "spend asks";
    default:
      return "items";
  }
}

// Just the headline figure as a proper KPI tile — result.meta entries are
// full sentences ("Budgeted 2.80B UGX", "Released against 2 confirmed
// asks"), not short label/value pairs, so they don't fit Stat's big-number
// styling (previously jammed a whole sentence into that slot). Meta renders
// as its own row of Pill chips instead, right below this strip.
function moneyStripFor(target: TraceTarget, result: TraceResult, moneyFn: (n: number) => string): MoneyStripItem[] {
  if (result.amount === undefined) return [];
  const label =
    target.kind === "spend"
      ? "Ask amount"
      : target.kind === "disbursement"
        ? "Tranche amount"
        : target.kind === "project" || target.kind === "subProject"
          ? "Spent so far"
          : "Amount";
  return [{ label, value: moneyFn(result.amount) }];
}

/**
 * Shown once ever (gated on localStorage), the first time a trace is opened.
 */
function FirstUseHint({ onDismiss }: { onDismiss: () => void }) {
  useEffect(() => {
    markSeen();
  }, []);
  return (
    <div className="mb-4 flex items-start gap-2 rounded-md border border-brand/30 bg-brandSoft px-3 py-2 text-xs text-ink">
      <p className="flex-1">Follow this figure to its source — the path above shows where you are.</p>
      <button
        onClick={onDismiss}
        aria-label="Dismiss this tip"
        className="rounded p-0.5 text-faint transition-colors hover:text-ink"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function TraceSkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      <div className="grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14" />
        ))}
      </div>
      <SkeletonText lines={3} />
      <SkeletonText lines={2} />
    </div>
  );
}

export function EvidenceSection({ result }: { result: TraceResult }) {
  if (result.evidence.length > 0) {
    return (
      <TraceSection label="Evidence · terminal">
        {result.evidence.map((e) => (
          <EvidenceInline key={e.id} item={e} />
        ))}
      </TraceSection>
    );
  }

  const below = result.evidenceBelow ?? 0;
  if (below > 0) {
    // Ancestor-level nodes (disbursement/milestone/project) don't hold evidence
    // themselves — it lives on their descendant spend asks. Rather than a
    // dead-end pointer, surface those asks directly so the user can jump
    // straight to the terminal evidence instead of drilling one row at a time.
    return (
      <TraceSection label="Evidence">
        <p className="px-1 pb-1 text-xs text-faint">
          Held on {below} spend ask{below === 1 ? "" : "s"} below — walk into one to see it inline.
        </p>
      </TraceSection>
    );
  }

  return (
    <TraceSection label="Evidence · terminal">
      <p className="px-1 py-2 text-sm text-faint">
        No evidence recorded yet. Reporters submit evidence from the field PWA; it will appear here within a minute of submission.
      </p>
    </TraceSection>
  );
}

function TraceSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-faint">{label}</h4>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

export function TraceRow({
  node,
  money: moneyFn,
  onNavigate,
}: {
  node: TraceResult["up"][number];
  money: (n: number) => string;
  onNavigate: (t: TraceTarget) => void;
}) {
  const clickable = !!node.target;
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      onClick={clickable ? () => onNavigate(node.target!) : undefined}
      className={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left ${
        clickable
          ? "cursor-pointer border-line transition-colors hover:border-strong hover:bg-subtle/60"
          : "border-line/60 bg-subtle/30"
      }`}
    >
      <div className="min-w-0 flex-1">
        <p className={`truncate text-sm font-medium ${clickable ? "text-ink" : "text-mute"}`}>{node.label}</p>
        {node.sublabel && <p className="truncate text-xs text-faint">{node.sublabel}</p>}
      </div>
      {node.assurance && <AssuranceBadge level={node.assurance} />}
      {node.amount !== undefined && <span className="text-sm tabular-nums text-ink">{moneyFn(node.amount)}</span>}
    </Comp>
  );
}
